import React from 'react';
import { useTranslation } from 'react-i18next';

import OutcomeImages from 'shared/components/outcomes/OutcomeImages';
import ResultOddChip from 'shared/components/outcomes/ResultOddChip';
import { getSportColor, getSportOnColor } from 'shared/utils/sport-colors';
import { vsMarketTypeKey } from 'lib/virtualSportsI18n';

import { formatEventTimeToHHMM } from 'features/results/formatEventTime';
import ResultCard from './ResultCard';

function normalizeRunnerCode(raw: unknown): string {
  // OutcomeImages expects:
  // - single runner: "1"
  // - combos: "1-2" / "1-2-3"
  const s = String(raw ?? '').trim();
  if (!s) return '';
  // Backend often uses "_" separators for combos; normalize to "-"
  const parts = s.split('_').map((p) => p.trim()).filter(Boolean);
  return parts.length > 1 ? parts.join('-') : s.replace(/_/g, '');
}

export default function RacingResultItems(props: any) {
  const { t } = useTranslation();
  const { current_result = {}, odds_format = 'decimal', market_type_groups, eventId: eventIdProp, eventTimestamp: eventTimestampProp } = props;
  const { odds = [], n_runners = 6, sport_id = 10, event_id, event_timestamp } = current_result as any;

  const eventId = eventIdProp ?? event_id;
  const eventTimestamp = eventTimestampProp ?? event_timestamp;
  const timeStr = formatEventTimeToHHMM(eventTimestamp);
  const headerText = timeStr
    ? `${t('RESULTS_AND_ODDS') || 'Results & Odds'} for event #${String(eventId ?? '')} ${timeStr}`
    : `${t('RESULTS_AND_ODDS') || 'Results & Odds'} for event #${String(eventId ?? '')}`;

  // Combination Forecast (505) and Combination Tricast (507): show one outcome and one odd only
  const ungrouped_markets = [1005, 1105, 2005, 2105, 1007, 1107, 2007, 2107];
  const market_type_ids = Object.values(market_type_groups || {}).map(({ market_type_id }: any) => market_type_id);

  const results_list = Object.entries(current_result as any)
    .filter(([market_type_id]) => market_type_ids.includes(market_type_id as any))
    .map(([market_type_id, outcomes], key) => {
      const market_type_group = Object.values(market_type_groups || {}).find(
        ({ market_type_id: id }: any) => id == market_type_id
      ) as any;
      // Use long market name (no abbreviation)
      const name = t(vsMarketTypeKey(String(market_type_id), false));
      const is_ungrouped = ungrouped_markets.includes(Number.parseInt(String(market_type_id), 10));

      const filtered_outcome = is_ungrouped ? [(outcomes as any)?.[0]] : (outcomes as any);
      const outcomeList = Array.isArray(filtered_outcome) ? filtered_outcome : [];
      const hasOdds = (odds as any) && Object.prototype.hasOwnProperty.call(odds, market_type_id);

      return (
        <ResultCard key={key} data-event-results-market={market_type_id}>
          {outcomeList.length === 0 ? (
            <div className="flex items-center gap-2 min-h-[2rem]">
              <span className="text-sm font-medium text-foreground truncate shrink-0 w-28 md:w-36">{name}</span>
              <span className="text-muted-foreground text-sm">—</span>
            </div>
          ) : (
            outcomeList.map((outcome: any, index: number) => {
              const odd = hasOdds ? (odds as any)?.[market_type_id]?.[outcome] : undefined;
              const code = normalizeRunnerCode((current_result as any)?.outcome_types?.[String(outcome)]?.code ?? outcome);
              return (
                <div
                  key={index}
                  className="flex items-center gap-2 min-h-[2rem]"
                  style={index > 0 ? { marginTop: '0.25rem' } : undefined}
                >
                  <span className="text-sm font-medium text-foreground truncate shrink-0 w-28 md:w-36">
                    {index === 0 ? name : '\u00A0'}
                  </span>
                  <span className="inline-flex items-center gap-1.5 shrink-0">
                    {code ? (
                      <OutcomeImages
                        outcome={code}
                        n_runners={n_runners}
                        sport_id={sport_id}
                        br={0}
                      />
                    ) : (
                      <span className="text-muted-foreground text-sm">—</span>
                    )}
                  </span>
                  <span className="ml-auto shrink-0">
                    <ResultOddChip odd={odd} oddsFormat={odds_format} />
                  </span>
                </div>
              );
            })
          )}
        </ResultCard>
      );
    });

  const sportColor = getSportColor(sport_id);
  const sportOnColor = getSportOnColor(sport_id);

  return (
    <div className="racing-result-items py-1">
      <div
        className="text-center py-2 rounded-t-lg"
        style={{ backgroundColor: sportColor, color: sportOnColor }}
      >
        <span className="font-bold">{headerText}</span>
      </div>
      <div className="bg-card text-card-foreground border border-border rounded-b-lg p-2">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {results_list}
        </div>
      </div>
    </div>
  );
}

